import PropTypes from 'prop-types';
import React from 'react';
import ProcessDataTable from 'components/core/ProcessDataTable';
import RIEDatePicker from 'components/core/editable/RIEDatePicker';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import RIENumber from 'components/core/editable/RIENumber';
import DataLocation from 'components/core/editable/DataLocation';
import Constants from 'utils/Constants';
import ProcessUtils from 'utils/ProcessUtils';
import ModalsService from 'services/ModalsService';
import DodService from 'services/DodService';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import Toolbar from 'components/core/toolbar/Toolbar';
import _ from 'lodash';

export default class RulesTable extends ProcessDataTable {
    static propTypes = {
        inputDatasetName: PropTypes.string
    };

    onRuleFieldUpdated = (fieldPath, fieldValue) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.updateField(fieldPath, fieldValue);
        //editor.mergeInputDatasets();
        editor.commit();
    };

    onRunLocationChange = (fieldPath, fieldValue) => {
        const location = fieldValue.value;
        const parts = location.split(' ');
        const value = {
            site: parts[0],
            fac: parts[1] ? parts[1] : null
        };

        const editor = ProcessUtils.getEditor(this.props.process);
        editor.updateField(fieldPath, value);
        //editor.mergeInputDatasets();
        editor.commit();
    };

    onDataLocationChange = (fieldPath, fieldValue) => {
        // We have to make sure the variables for the datastream are loaded in the cache
        DodService.fetchDodVarsIfNeeded(fieldValue.datastream_name);

        const finishWhenDatastreamLoaded = datastream => {
            const datastreamVariables = DodService.getMyState(DodService.getStore().getState()).dodsById[datastream];
            if (datastreamVariables === null || typeof(datastreamVariables) === 'undefined') {
                setTimeout(() => {
                    finishWhenDatastreamLoaded(datastream);
                }, 100);

            } else {
                const editor = ProcessUtils.getEditor(this.props.process);

                // Set the values for the data location
                const datastreamName = fieldValue.datastream_name;
                const datastreamNamePath = fieldPath.concat(['datastream_name']);
                const dataLocationPath = fieldPath.concat(['data_location']);
                _.set(editor.process, datastreamNamePath, datastreamName);
                _.set(editor.process, dataLocationPath, { site: fieldValue.site, fac: fieldValue.fac });

                const datasetName = this.props.inputDatasetName;

                // Now fix the variable mappings
                const rule = _.get(editor.process, fieldPath);
                const mappings = rule.variable_mappings;
                Object.keys(mappings).forEach(assignedVariableName => {
                    const datastreamVariableNames = ProcessUtils.guessInputDatastreamVariableNames(this.props.process, datasetName, assignedVariableName, datastreamName);
                    mappings[assignedVariableName] = datastreamVariableNames;
                });

                //editor.mergeInputDatasets();
                editor.syncInputStreams();
                editor.commit();

            }
        };

        finishWhenDatastreamLoaded(fieldValue.datastream_name);

    };

    onPriorityChanged = (oldPriority, fieldPath, value) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        const rules = editor.process.variable_retrieval.input_datasets[this.props.inputDatasetName].rules;
        let newPriority = Number(value);

        // Make sure the typed value is valid
        if (newPriority > rules.length) {
            newPriority = rules.length;
        } else if (newPriority < 1) {
            newPriority = 1;
        }

        const oldIndex = oldPriority - 1;
        const newIndex = newPriority - 1;

        const movedRule = rules.splice(oldIndex, 1)[0]; // remove the rule at its old position
        rules.splice(newIndex, 0, movedRule); // insert it at the new position

        // re-assign the priority fields
        rules.forEach((rule, index) => {
            rule.priority = index + 1;
        });

        // Update any coordinate system that is mapped to this input dataset so the datastreams' coordinate variables are in the right order
        // (Note it's pretty unlikely that the datastreams will have different coordinate variables, so generally this check won't matter)
        ProcessUtils.visitDimensions(editor.process, (dimension) => {
            if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING &&
                dimension.input_dataset === this.props.inputDatasetName) {
                const coordinateVariablesArray = dimension.coordinate_variables_per_rule;
                const movedVariables = coordinateVariablesArray.splice(oldIndex, 1)[0]; // remove the variables [] at its old position
                coordinateVariablesArray.splice(newIndex, 0, movedVariables); // insert it at the new position
            }
        });

        //editor.mergeInputDatasets();
        editor.commit();
    };

    onDeleteRules = (selectedItems) => {
        const selectedIds = Object.keys(selectedItems);

        // Check if the user is trying to delete the last row.  If this is the case, then we don't allow the delete because
        // All variables must have at least one retrieval rule defined.
        let inputDataset = this.props.process.variable_retrieval.input_datasets[this.props.inputDatasetName];
        let rules = inputDataset.rules;
        if (selectedIds.length === rules.length) {
            // pop up error message saying the edit was reverted
            ModalsService.openModal({
                id: 'delete_rules_warning_message',
                modalType: ModalsService.TYPE_ERROR,
                modalTitle: 'Delete Rules Failed',
                modalMessage: 'All variables must have at least one retrieval rule defined.'
            });

        } else {

            const onOk = () => {
                const editor = ProcessUtils.getEditor(this.props.process);
                inputDataset = editor.process.variable_retrieval.input_datasets[this.props.inputDatasetName];
                rules = inputDataset.rules;
                const deletedIndexes = [];

                // sort so that we remove the last ones first so not to mess up the index
                selectedIds.sort((a, b) => { return b - a; });

                for (let i = 0; i < selectedIds.length; i++) {
                    const ruleIndex = selectedIds[i] - 1;
                    deletedIndexes.push(ruleIndex);
                    rules.splice(ruleIndex, 1);
                }

                //update priority
                rules.forEach((rule, index) => {
                    rule.priority = index + 1;
                });

                //editor.mergeInputDatasets();
                editor.syncDeletedRulesWithCoordinateSystem(inputDataset.name, deletedIndexes);
                editor.commit();
                this.clearSelection();
            };

            // TODO: if there are coordinate system mappings to this input dataset, should we show a warning message to
            // the user?
            if (selectedIds.length > 0) {
                ModalsService.openModal({
                    id: 'confirm_delete_retrieval_rules',
                    modalType: ModalsService.TYPE_CONFIRM,
                    modalTitle: 'Confirm Delete',
                    modalMessage: 'Are you sure you want to delete the selected retrieval rule(s)?',
                    onOk
                });
            }
        }
    };

    getRule(priority) {
        const rules = this.rowData;
        let r = null;
        rules.forEach(rule => {
            if (rule.priority === priority) {
                r = rule;
            }
        });
        return r;
    }

    getRulePath(priority) {
        const index = priority - 1;
        return this.getInputDatasetPath().concat(['rules', index]);
    }

    getInputDatasetPath() {
        return ['variable_retrieval', 'input_datasets', this.props.inputDatasetName];
    }

    renderPriority(priority) {
        const onPriorityChanged = this.onPriorityChanged.bind(this, priority);
        return (
            <RIENumber
                value={priority}
                change={onPriorityChanged}
                fieldPath={this.getRulePath(priority).concat(['priority'])}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                processErrors={this.processErrors}
            />
        );
    }

    renderRunLocation(priority) {
        const rule = this.getRule(priority);
        let locationString = rule.run_location.site.toUpperCase();
        locationString += rule.run_location.fac ? ` ${rule.run_location.fac}` : '';
        const runLocation = Constants.convertValueToSelectValue(locationString, 'runLocation');

        return (
            <RIEComboBox
                value={runLocation}
                change={this.onRunLocationChange}
                fieldPath={this.getRulePath(priority).concat(['run_location'])}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                options={this.runLocationOptions}
            />
        );
    }

    renderStartTime(priority) {
        const rule = this.getRule(priority);
        return (
            <RIEDatePicker
                value={rule.run_time.start}
                change={this.onRuleFieldUpdated}
                fieldPath={this.getRulePath(priority).concat(['run_time', 'start'])}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
            />
        );
    }

    renderEndTime(priority) {
        const rule = this.getRule(priority);
        return (
            <RIEDatePicker
                value={rule.run_time.end}
                change={this.onRuleFieldUpdated}
                fieldPath={this.getRulePath(priority).concat(['run_time', 'end'])}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
            />
        );
    }

    renderDataLocation(priority) {
        const rule = this.getRule(priority);
        const dataLocationValue = {
            site: rule.data_location.site.toUpperCase(),
            fac: rule.data_location.fac,
            datastream_name: rule.datastream_name
        };

        return (
            <DataLocation
                options={{ data_locations: this.dataLocationOptions, datastreams: this.datastreams }}
                value={dataLocationValue}
                change={this.onDataLocationChange}
                fieldPath={this.getRulePath(priority)}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
            />
        );
    }

    renderToolbar() {
        return (
            <Toolbar selectedItems={this.getSelectedItems()}>
                <ToolbarItem
                    title="Delete"
                    className="fa fa-times delete"
                    clickHandler={this.onDeleteRules}
                    selectionDependent
                    enabled={this.props.hasWritePermissions}
                />
                <ToolbarItem
                    title="New"
                    className="fa fa-plus new"
                    clickHandler={ProcessUtils.newInputDatasetRetrievalRuleFunction(this.props.process, this.props.inputDatasetName)}
                    enabled={this.props.hasWritePermissions}
                />
            </Toolbar>
        );
    }

    getRowKey() {
        return 'priority';
    }

    /**
     * Must be defined as an array of objects (not a map)
     */
    getTableRowData(props) {
        const inputDataset = props.process.variable_retrieval.input_datasets[props.inputDatasetName];
        return inputDataset.rules;
    }

    getTableColumns() {
        return [
            {
                property: 'priority',
                header: {
                    label: 'Priority'
                },
                cell: {
                    formatters: [
                        priority => { return this.renderPriority(priority); }
                    ]
                }
            },
            {
                property: 'priority',
                header: {
                    label: 'If process runs at:'
                },
                cell: {
                    formatters: [
                        priority => { return this.renderRunLocation(priority); }
                    ]
                }
            },
            {
                property: 'priority',
                header: {
                    label: 'Between:'
                },
                cell: {
                    formatters: [
                        priority => { return this.renderStartTime(priority); }
                    ]
                }
            },
            {
                property: 'priority',
                header: {
                    label: 'And:'
                },
                cell: {
                    formatters: [
                        priority => { return this.renderEndTime(priority); }
                    ]
                }
            },
            {
                property: 'priority',
                header: {
                    label: 'Retrieve data from:'
                },
                cell: {
                    formatters: [
                        priority => { return this.renderDataLocation(priority); }
                    ]
                }
            }

        ];
    }

}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/inputs/RulesTable.js